import React, { useState } from "react";
import { Alert, Box, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { NewEntryForm } from "./NewEntryForm";

interface AddEntryComposerProps {
  speakers: string[];
  /** True while a new entry's range is being set on the video timeline. */
  isArmingNew: boolean;
  /** Speaker + content captured; the caller then arms the range selector. */
  onArmNew: (speaker: string, text: string) => void;
  onCancel: () => void;
  disabled?: boolean;
}

/**
 * Starts the add-new-entry flow: enter the speaker and content first, then drop
 * start/end markers on the video timeline (handled by the shared
 * TimeRangeSelector). The entry is inserted, sorted by start time, on commit.
 */
export const AddEntryComposer: React.FC<AddEntryComposerProps> = ({
  speakers,
  isArmingNew,
  onArmNew,
  onCancel,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);

  if (isArmingNew) {
    return (
      <Alert
        severity="info"
        sx={{ mb: 1.5 }}
        action={
          <Button color="inherit" size="small" onClick={onCancel}>
            Cancel
          </Button>
        }
      >
        Now set the new entry&apos;s start and end on the video timeline, then
        press Done.
      </Alert>
    );
  }

  if (!open) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
          disabled={disabled || speakers.length === 0}
          sx={{ textTransform: "none" }}
        >
          Add transcript entry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1.5 }}>
      <NewEntryForm
        speakers={speakers}
        onSubmit={(speaker, text) => {
          onArmNew(speaker, text);
          setOpen(false);
        }}
        onCancel={() => setOpen(false)}
      />
    </Box>
  );
};
